import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, CircularProgress,
  IconButton, FormControlLabel, Checkbox, FormGroup,
  Select, MenuItem, InputLabel, FormControl, Switch, LinearProgress,
  Divider,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  AddPhotoAlternate as AddPhotoIcon,
  VideoCall as VideoCallIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: 8,
  },
  dialogPaper: {
    minWidth: 600,
  },
  formField: {
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    marginBottom: 8,
    marginTop: 4,
  },
  platformGroup: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 10,
  },
  platformHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    fontWeight: 600,
    fontSize: 13,
  },
  platformIcon: {
    width: 20,
    height: 20,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  subCheckbox: {
    marginLeft: 28,
    "& .MuiFormControlLabel-label": { fontSize: 13 },
  },
  uploadBox: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 20,
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.2s",
    marginBottom: 16,
    "&:hover": { borderColor: theme.palette.primary.main },
  },
  previewBox: {
    position: "relative",
    marginBottom: 16,
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  previewImg: {
    width: "100%",
    maxHeight: 200,
    objectFit: "contain",
    display: "block",
    backgroundColor: "#f5f5f5",
  },
  previewVideo: {
    width: "100%",
    maxHeight: 200,
    display: "block",
    backgroundColor: "#111",
  },
  removeBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    padding: 4,
    "&:hover": { backgroundColor: "rgba(200,0,0,0.8)" },
  },
  infoBox: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 8,
    padding: "10px 14px",
    marginTop: 4,
  },
  waConnectionBox: {
    marginTop: 8,
    marginLeft: 28,
    padding: "8px 12px",
    backgroundColor: theme.palette.action.hover,
    borderRadius: 6,
  },
}));

// Grupos de plataforma com os tipos de post de cada uma
const PLATFORM_GROUPS = [
  {
    key: "whatsapp",
    label: "WhatsApp",
    color: "#25D366",
    emoji: "💬",
    types: [
      { key: "whatsapp_status", label: "Status do WhatsApp" },
    ],
    needsConnection: true,
  },
  {
    key: "instagram",
    label: "Instagram",
    color: "#E1306C",
    emoji: "📸",
    types: [
      { key: "instagram_feed",  label: "Feed do Instagram" },
      { key: "instagram_story", label: "Story do Instagram" },
    ],
    needsConnection: false,
  },
  {
    key: "facebook",
    label: "Facebook",
    color: "#1877F2",
    emoji: "👤",
    types: [
      { key: "facebook_feed",  label: "Feed do Facebook" },
      { key: "facebook_story", label: "Story do Facebook" },
    ],
    needsConnection: false,
  },
];

const MEDIA_TYPES = [
  { key: "none",  label: "Apenas texto" },
  { key: "image", label: "Imagem" },
  { key: "video", label: "Vídeo" },
];

const EMPTY_FORM = {
  title: "",
  content: "",
  mediaType: "none",
  mediaUrl: "",
  postTypes: [],       // ex: ['whatsapp_status', 'instagram_feed']
  whatsappIds: [],
  scheduled: false,
  scheduledAt: "",
};

const PosterModal = ({ open, onClose, post = null, onSuccess }) => {
  const classes = useStyles();
  const fileRef = useRef(null);
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  const isEditing = !!post;

  // Carrega conexões WhatsApp ao abrir
  useEffect(() => {
    if (!open) return;
    setLoadingConnections(true);
    api.get("/whatsapp", { params: { companyId: user.companyId, session: 0 } })
      .then(({ data }) => {
        const CANAIS_COM_STATUS = ["whatsapp", "baileys", "", null, undefined];
        setConnections(
          (data || []).filter(w => CANAIS_COM_STATUS.includes(w.channel))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingConnections(false));
  }, [open, user.companyId]);

  // Popula form ao editar
  useEffect(() => {
    if (!open) return;
    if (post) {
      const scheduled = !!post.scheduledAt;
      setForm({
        title: post.title || "",
        content: post.content || "",
        mediaType: post.mediaType || "none",
        mediaUrl: post.mediaUrl || "",
        postTypes: post.platforms || [],   // campo platforms guarda os tipos específicos
        whatsappIds: post.whatsappIds || [],
        scheduled,
        scheduledAt: scheduled
          ? new Date(post.scheduledAt).toISOString().slice(0, 16)
          : "",
      });
      setMediaPreview(
        post.mediaUrl
          ? (post.mediaUrl.startsWith("http")
            ? post.mediaUrl
            : `${process.env.REACT_APP_BACKEND_URL || ""}${post.mediaUrl}`)
          : null
      );
    } else {
      setForm(EMPTY_FORM);
      setMediaPreview(null);
    }
    if (fileRef.current) fileRef.current.value = "";
  }, [open, post]);

  const handleField = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const togglePostType = (typeKey) => {
    setForm(prev => ({
      ...prev,
      postTypes: prev.postTypes.includes(typeKey)
        ? prev.postTypes.filter(t => t !== typeKey)
        : [...prev.postTypes, typeKey],
    }));
  };

  const handleWhatsappToggle = (id) => {
    setForm(prev => ({
      ...prev,
      whatsappIds: prev.whatsappIds.includes(id)
        ? prev.whatsappIds.filter(w => w !== id)
        : [...prev.whatsappIds, id],
    }));
  };

  const handleMediaTypeChange = (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, mediaType: value, mediaUrl: "" }));
    setMediaPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) { toast.error("Selecione apenas imagem ou vídeo"); return; }
    if (isImage && file.size > 5 * 1024 * 1024) { toast.error("Imagem: máximo 5 MB"); return; }
    if (isVideo && file.size > 100 * 1024 * 1024) { toast.error("Vídeo: máximo 100 MB"); return; }

    setMediaPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const tipoPeloArquivo = isVideo ? "video" : "image";
      setForm(prev => ({
        ...prev,
        mediaUrl: data.filePath,
        mediaType: tipoPeloArquivo
      }));
    } catch (err) {
      toastError(err);
      setMediaPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMedia = () => {
    setMediaPreview(null);
    setForm(prev => ({ ...prev, mediaUrl: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error("Título é obrigatório"); return; }
    if (form.postTypes.length === 0) { toast.error("Selecione pelo menos um tipo de publicação"); return; }

    const needsWA = form.postTypes.includes("whatsapp_status");
    if (needsWA && connections.length === 0) {
      toast.error(
        "Status do WhatsApp só funciona em conexão por QR Code. A API oficial da Meta não permite publicar status."
      );
      return;
    }
    if (needsWA && form.whatsappIds.length === 0) {
      toast.error("Selecione ao menos uma conexão WhatsApp"); return;
    }
    if (form.scheduled && !form.scheduledAt) {
      toast.error("Defina a data e hora do agendamento"); return;
    }
    if (form.mediaType !== "none" && !form.mediaUrl) {
      toast.error("Aguarde o upload da mídia ou remova-a"); return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim() || undefined,
        mediaType: form.mediaType,
        mediaUrl: form.mediaUrl || undefined,
        platforms: form.postTypes,   // salva tipos específicos no campo platforms
        whatsappIds: form.whatsappIds,
        scheduledAt: form.scheduled ? form.scheduledAt : null,
      };

      let result;
      if (isEditing) {
        const { data } = await api.put(`/social-posts/${post.id}`, payload);
        result = data;
        toast.success("Post atualizado com sucesso!");
      } else {
        const { data } = await api.post("/social-posts", payload);
        result = data;
        toast.success("Post criado com sucesso!");
      }
      onSuccess(result, isEditing);
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving && !uploading) onClose();
  };

  const hasNonWhatsApp = form.postTypes.some(t => t.startsWith("instagram") || t.startsWith("facebook"));
  const hasWhatsApp = form.postTypes.includes("whatsapp_status");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6">{isEditing ? "Editar Post" : "Novo Post"}</Typography>
        <IconButton onClick={handleClose} disabled={saving || uploading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 16 }}>

        {/* Título */}
        <TextField
          label="Título *"
          variant="outlined"
          fullWidth
          value={form.title}
          onChange={handleField("title")}
          className={classes.formField}
          disabled={saving}
          inputProps={{ maxLength: 200 }}
          autoFocus
        />

        {/* Texto / Legenda */}
        <TextField
          label="Texto / Legenda"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          value={form.content}
          onChange={handleField("content")}
          className={classes.formField}
          disabled={saving}
          placeholder="Texto do status, legenda da imagem/vídeo..."
        />

        {/* Mídia */}
        <Typography className={classes.sectionLabel}>Mídia</Typography>
        <FormControl variant="outlined" fullWidth size="small" className={classes.formField} disabled={saving}>
          <InputLabel>Tipo de mídia</InputLabel>
          <Select value={form.mediaType} onChange={handleMediaTypeChange} label="Tipo de mídia">
            {MEDIA_TYPES.map(m => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>

        {form.mediaType !== "none" && (
          <>
            {uploading && <LinearProgress style={{ marginBottom: 12, borderRadius: 4 }} />}
            {mediaPreview ? (
              <Box className={classes.previewBox}>
                {form.mediaType === "image"
                  ? <img src={mediaPreview} alt="Preview" className={classes.previewImg} />
                  : <video src={mediaPreview} className={classes.previewVideo} controls />
                }
                <IconButton size="small" className={classes.removeBtn} onClick={handleRemoveMedia} disabled={saving || uploading}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                className={classes.uploadBox}
                onClick={() => !saving && !uploading && fileRef.current?.click()}
              >
                {form.mediaType === "image"
                  ? <AddPhotoIcon style={{ fontSize: 40, opacity: 0.35, display: "block", margin: "0 auto 8px" }} />
                  : <VideoCallIcon style={{ fontSize: 40, opacity: 0.35, display: "block", margin: "0 auto 8px" }} />
                }
                <Typography variant="body2" color="textSecondary">
                  {uploading
                    ? "Enviando..."
                    : `Clique para selecionar ${form.mediaType === "image" ? "uma imagem (máx. 5 MB)" : "um vídeo (máx. 100 MB)"}`
                  }
                </Typography>
              </Box>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={form.mediaType === "image" ? "image/*" : "video/*"}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </>
        )}

        {/* Onde publicar */}
        <Typography className={classes.sectionLabel}>Onde publicar *</Typography>

        {PLATFORM_GROUPS.map(group => {
          const anySelected = group.types.some(t => form.postTypes.includes(t.key));
          return (
            <Box key={group.key} className={classes.platformGroup}>
              {/* Cabeçalho do grupo */}
              <Box className={classes.platformHeader} style={{ color: group.color }}>
                <span>{group.emoji}</span>
                <span>{group.label}</span>
              </Box>

              {/* Sub-checkboxes por tipo */}
              <FormGroup>
                {group.types.map(type => (
                  <FormControlLabel
                    key={type.key}
                    className={classes.subCheckbox}
                    control={
                      <Checkbox
                        checked={form.postTypes.includes(type.key)}
                        onChange={() => togglePostType(type.key)}
                        color="primary"
                        disabled={saving}
                        size="small"
                      />
                    }
                    label={type.label}
                  />
                ))}
              </FormGroup>

              {/* Conexões WhatsApp (se selecionou status do WA) */}
              {group.needsConnection && form.postTypes.includes("whatsapp_status") && (
                <Box className={classes.waConnectionBox}>
                  <Typography style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", marginBottom: 4 }}>
                    Conexões a usar
                  </Typography>
                  {loadingConnections ? (
                    <CircularProgress size={16} />
                  ) : connections.length === 0 ? (
                    <Typography variant="caption" style={{ color: "#d32f2f" }}>
                      Nenhuma conexão WhatsApp ativa encontrada
                    </Typography>
                  ) : (
                    <FormGroup>
                      {connections.map(c => (
                        <FormControlLabel
                          key={c.id}
                          control={
                            <Checkbox
                              checked={form.whatsappIds.includes(c.id)}
                              onChange={() => handleWhatsappToggle(c.id)}
                              color="primary"
                              disabled={saving}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">{c.name}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  )}
                </Box>
              )}
            </Box>
          );
        })}

        {/* Aviso Meta API */}
        {hasNonWhatsApp && (
          <Box className={classes.infoBox}>
            <Typography variant="caption" style={{ color: "#c2410c" }}>
              <strong>Instagram / Facebook:</strong> A publicação automática requer configuração das credenciais da Meta API em <strong>Configurações &gt; Integrações</strong>. O post será salvo e ficará como "rascunho" até a integração ser configurada.
            </Typography>
          </Box>
        )}

        <Divider style={{ margin: "16px 0 12px" }} />

        {/* Agendamento */}
        <FormControlLabel
          control={
            <Switch
              checked={form.scheduled}
              onChange={e => {
                const checked = e.target.checked;
                setForm(prev => ({ ...prev, scheduled: checked, scheduledAt: "" }));
              }}
              color="primary"
              disabled={saving}
            />
          }
          label="Agendar publicação"
          style={{ marginBottom: 4 }}
        />
        {form.scheduled && (
          <TextField
            label="Data e hora *"
            type="datetime-local"
            variant="outlined"
            fullWidth
            value={form.scheduledAt}
            onChange={handleField("scheduledAt")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            disabled={saving}
            style={{ marginTop: 12 }}
          />
        )}

      </DialogContent>

      <DialogActions style={{ padding: "12px 16px", borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={handleClose} disabled={saving || uploading} startIcon={<CancelIcon />}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving || uploading}
          startIcon={saving ? <CircularProgress size={18} /> : (form.scheduled ? <ScheduleIcon /> : <SaveIcon />)}
        >
          {saving ? "Salvando..." : isEditing ? "Atualizar" : form.scheduled ? "Agendar" : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PosterModal;
